/** Card 5 — nights: sundowning, sleep changes, night wandering. */
import React, { useState } from "react";
import { TransitionPanel, DemoBadge, trackStep, trackCompleted } from "../activationShared";

const TOPICS = [
    {
        key: "sundowning",
        label: "Evening agitation (sundowning)",
        what: "Late afternoon and evening confusion, restlessness, or anger is called sundowning. As daylight fades, a tired brain loses its anchors — shadows look wrong, routines blur, and anxiety fills the gap. It's one of the most common and most exhausting patterns in dementia.",
        tonight: [
            "Turn lights ON before dusk — bright, even light shrinks shadows and cues \"not bedtime yet.\"",
            "Front-load the day: appointments and baths before 2 PM; keep evenings boringly calm.",
            "Try a familiar anchor at the witching hour — the same music, folding towels, a photo album.",
        ],
        lane: { q: "Mom gets furious every day around 5 PM. I dread evenings now.", a: "That daily 5 PM storm has a name — sundowning — and dreading it is completely understandable. Let's look at what happens in the hour before: light, noise, hunger, and fatigue are the usual triggers, and evenings usually soften once we adjust two or three of them. What does 4 PM in your house look like?" },
    },
    {
        key: "sleep",
        label: "Sleep changes",
        what: "Dementia disrupts the brain's internal clock. Naps stretch longer, nights fragment, and days and nights can fully flip. Poor sleep then worsens confusion — a loop that wears out the caregiver first.",
        tonight: [
            "Morning sunlight (even 20 minutes by a bright window) is the strongest clock-setter there is.",
            "Cap daytime naps around 30 minutes, before 2 PM.",
            "Keep one wind-down ritual in the same order nightly — brain clocks love repetition. Ask the doctor before any sleep aid; many worsen confusion.",
        ],
        lane: { q: "Dad sleeps all day and is wide awake at 2 AM. I'm falling apart.", a: "Day-night reversal is brutal because you can't sleep in shifts alone. Two things at once: re-anchor his clock (morning light, shorter naps, movement before lunch) — and protect YOUR sleep, because this only works if you're standing. Is there anyone who could cover even two nights a week while we reset his rhythm?" },
    },
    {
        key: "wandering",
        label: "Night wandering",
        what: "Waking disoriented, your loved one may get up \"to go to work\" or \"go home\" — even at 3 AM. Wandering is dangerous, but it's almost always purposeful to them: they're looking for something familiar.",
        tonight: [
            "Make the path safe tonight: nightlights to the bathroom, door chime or bell on exit doors, car keys out of sight.",
            "If you find them mid-wander, don't quiz (\"do you know what time it is?\") — walk with them a moment, then redirect: \"It's cold out, let's have tea first.\"",
            "Consider an ID bracelet and alert the neighbors — 6 in 10 people with dementia will wander at least once.",
        ],
        lane: { q: "I found my husband outside at 3 AM in his pajamas. I'm terrified.", a: "That moment — the open door, the empty bed — is one of the scariest a caregiver can have. You got him back; now let's make sure tonight is safer: a chime on that door, lights along his path, and a plan for where he tends to head. He was likely looking for something familiar. Do you know what he says he's trying to reach?" },
    },
];

export default function NightsDemo({ onClose }) {
    const [topic, setTopic] = useState(null);
    const t = TOPICS.find((x) => x.key === topic);

    return (
        <div data-testid="activation-nights">
            <DemoBadge>Short guide — anonymous, nothing is saved</DemoBadge>
            <h3 className="mt-4 font-heading font-medium text-lg md:text-xl text-[#1F2624]">What do nights look like at your house?</h3>
            <div className="mt-3 flex flex-wrap gap-2">
                {TOPICS.map((x) => (
                    <button key={x.key} type="button" data-testid={`nights-${x.key}`}
                        onClick={() => { setTopic(x.key); trackStep("nights", x.key); trackCompleted("nights"); }}
                        className={`rounded-2xl px-4 py-2 text-sm font-medium border transition-colors ${topic === x.key
                            ? "bg-teal-dark text-white border-teal-dark"
                            : "bg-white text-[#2A3330] border-[#E2E8F0] hover:border-teal/50"}`}>
                        {x.label}
                    </button>
                ))}
            </div>
            {t && (
                <div className="mt-4" data-testid="nights-content">
                    <p className="text-sm text-[#2A3330] leading-relaxed">{t.what}</p>
                    <p className="mt-3 text-sm font-semibold text-[#1F2624]">Three things to try tonight:</p>
                    <ul className="mt-2 space-y-1.5">
                        {t.tonight.map((s) => (
                            <li key={s} className="text-sm text-[#4C5551] leading-relaxed pl-4 relative">
                                <span className="absolute left-0 top-[7px] w-1.5 h-1.5 rounded-full bg-teal" />{s}
                            </li>
                        ))}
                    </ul>
                    <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-[#6E7A74]">How this sounds with Lane</p>
                    <div className="mt-2 rounded-2xl rounded-br-md bg-teal-dark text-white px-4 py-3 text-sm ml-auto max-w-[85%] w-fit">{t.lane.q}</div>
                    <div className="mt-2 rounded-2xl rounded-bl-md bg-cream-light border border-[#EDE6D6] px-4 py-3 text-sm text-[#2A3330] leading-relaxed max-w-[92%]">{t.lane.a}</div>
                    <p className="mt-2 text-xs text-[#6E7A74] italic">Sample conversation — Lane&apos;s real answers remember what you&apos;ve already tried.</p>
                    <TransitionPanel card="nights" onClose={onClose} />
                </div>
            )}
        </div>
    );
}
